import { isEmpty } from 'radash';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { useDataModel } from '@/hooks/useDataModel';
import { useOrganization } from '@/hooks/useOrganization';
import { useUser } from '@/hooks/useUser';
import { getCustomLayoutFilter } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';
import { DataModelAttributeTypeName, Item, LayoutFilterAttribute, LayoutSection } from '@/types/DataModel';

import { useAdditionalCategoryAttributes } from './useAdditionalCategoryAttributes';
import { useLayout } from './useLayout';

const useSections = (category: string | undefined) => {

    const [shouldUpdateSections, setShouldUpdateSections] = useState(false);
    const [lastSectionsCategory, setLastSectionsCategory] = useState<string>();
    const [layoutSections, setLayoutSections] = useState<LayoutSection[]>();

    const { layout } = useLayout();
    const { dataModel } = useDataModel();
    const { getAdditionalCategoryAttributes } = useAdditionalCategoryAttributes();

    const { watch, getValues } = useFormContext();

    const { organization } = useOrganization();
    const { user } = useUser();

    const loadLayoutSections = async () => {

        if (layout && category && dataModel && user && organization) {

            const item = getValues() as Item;
            await getAdditionalCategoryAttributes(item);

            let additionalSectionAttributes: Record<string, (LayoutFilterAttribute | string)[]> = {};
            const layoutFilterName = layout.layoutOptions?.layoutFilter;
            if (layoutFilterName) {
                const layoutFilterFn = getCustomLayoutFilter(layoutFilterName);
                if (layoutFilterFn) {
                    additionalSectionAttributes = layoutFilterFn(layout, item, user, organization);
                } else {
                    logger.warn(`Layout filter ${layoutFilterName} specified in layout ${layout.name} does not exist`);
                }
            }

            const sections = dataModel?.filteredSections(layout, { 'category__': category } as unknown as Item, additionalSectionAttributes);
            const updatedSections = sections.filter(section => !isEmpty(section.attributes));

            const setSectionAttributeParam = (layoutName: string, sectionName: string, attribute: any, paramName: string, defaultValue: any, item: Item) => {
                let paramValue;
                if (attribute.additionalParams) {
                    paramValue = attribute.additionalParams[paramName];
                } else {
                    paramValue = dataModel?.sectionAttributeParam(layoutName, item, sectionName, attribute.name, paramName);
                }
                if (paramValue === undefined || paramValue === null) {
                    paramValue = defaultValue;
                }
                attribute[paramName] = paramValue;
            };

            const sectionAttributeParams = [
                { name: 'hidden', defaultValue: null },
                { name: 'readonly', defaultValue: null },
                { name: 'labelWidth', defaultValue: 4 },
                { name: 'inputWidth', defaultValue: 8 },
                { name: 'iconSize', defaultValue: '1em' },
                { name: 'valuesFormat', defaultValue: ['label', 'key'] }
            ];

            updatedSections.forEach(section => {
                section.attributes.forEach((attribute: any) => {
                    sectionAttributeParams.forEach(({ name, defaultValue }) => {
                        setSectionAttributeParam(layout.name, section.name, attribute, name, defaultValue, item);
                    });
                });
            });

            setLayoutSections(updatedSections);
            setLastSectionsCategory(item.category__);
            setShouldUpdateSections(false);

        }
    };

    useEffect(() => {

        if (!category || category === lastSectionsCategory) {
            return;
        }

        // Load and setup layout sections, if either primary key is set, or category and layout are known and have changed
        loadLayoutSections();

        // We only update the editor sections when we update the category, as
        // the sections are dependent on:
        // - Layout: which don't change.
        // - Item Category.
        //eslint-disable-next-line
    }, [layout, category]);

    useEffect(() => {

        if (!shouldUpdateSections) {
            return;
        }
        loadLayoutSections();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldUpdateSections]);

    useEffect(() => {

        const itemChangesSubscription = watch((_, { name }) => {

            if (!dataModel || !name || isEmpty(name)) {
                return;
            }

            const attribute = dataModel?.attribute(name);
            if (attribute?.typeName === DataModelAttributeTypeName.AdditionalCategory) {
                setShouldUpdateSections(true);
            }
        });

        return () => itemChangesSubscription.unsubscribe();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { layoutSections };
};

export { useSections };
